import * as THREE from 'three';
import type { Persona, PersonaId } from '../ai/personas';

export interface IslandConfig {
  persona: Persona;
  position: THREE.Vector3;
}

export class Island {
  readonly group = new THREE.Group();
  readonly hitSphere: THREE.Sphere;
  readonly personaId: PersonaId;
  readonly persona: Persona;
  private readonly inhabitant: THREE.Group;
  private readonly bodyMesh: THREE.Mesh;
  private readonly accentParts: THREE.Object3D[] = [];
  private readonly inhabitantBaseMaterial: THREE.MeshStandardMaterial;
  private readonly accentMaterial: THREE.MeshStandardMaterial;
  private emojiSprite: THREE.Sprite | null = null;
  private emotionTimer = 0;
  private shakeTimer = 0;
  private shakeAmplitude = 0;
  private readonly origin: THREE.Vector3;
  private readonly bobPhase: number;
  private readonly idlePhase: number;

  constructor(cfg: IslandConfig) {
    this.personaId = cfg.persona.id;
    this.persona = cfg.persona;
    this.origin = cfg.position.clone();
    this.bobPhase = Math.random() * Math.PI * 2;
    this.idlePhase = Math.random() * Math.PI * 2;

    const baseColor = cfg.persona.color;

    const baseMat = new THREE.MeshStandardMaterial({ color: baseColor, flatShading: true, roughness: 0.85 });
    this.accentMaterial = new THREE.MeshStandardMaterial({ color: cfg.persona.accent, flatShading: true, roughness: 0.7, emissive: 0x000000 });

    const base = new THREE.Mesh(new THREE.IcosahedronGeometry(1.6, 0), baseMat);
    base.position.y = -0.6;
    base.scale.set(1.1, 0.55, 1.1);

    const top = new THREE.Mesh(new THREE.ConeGeometry(0.9, 1.2, 6), this.accentMaterial);
    top.position.y = 0.5;

    this.inhabitantBaseMaterial = new THREE.MeshStandardMaterial({
      color: cfg.persona.accent,
      flatShading: true,
      emissive: cfg.persona.color,
      emissiveIntensity: 0.18,
      roughness: 0.6
    });
    const built = this.buildInhabitant(cfg.persona.id);
    this.inhabitant = built.group;
    this.bodyMesh = built.body;
    this.accentParts.push(...built.accents);
    this.inhabitant.position.y = 1.4;

    this.group.add(base, top, this.inhabitant);
    this.group.position.copy(cfg.position);

    this.hitSphere = new THREE.Sphere(cfg.position.clone(), 3.6);
  }

  private buildInhabitant(id: PersonaId): { group: THREE.Group; body: THREE.Mesh; accents: THREE.Object3D[] } {
    const group = new THREE.Group();
    const accents: THREE.Object3D[] = [];
    let body: THREE.Mesh;
    switch (id) {
      case 'whale': {
        body = new THREE.Mesh(new THREE.IcosahedronGeometry(0.8, 0), this.inhabitantBaseMaterial);
        const tail = new THREE.Mesh(new THREE.ConeGeometry(0.35, 0.55, 4), this.accentMaterial);
        tail.position.set(-0.7, 0.05, 0);
        tail.rotation.z = Math.PI / 2;
        accents.push(tail);
        group.add(body, tail);
        break;
      }
      case 'jelly': {
        body = new THREE.Mesh(new THREE.SphereGeometry(0.6, 6, 2, 0, Math.PI * 2, 0, Math.PI / 2), this.inhabitantBaseMaterial);
        body.position.y = 0.1;
        const tentacles = new THREE.Group();
        const tentacleGeo = new THREE.CylinderGeometry(0.04, 0.02, 0.5, 3, 1, true);
        for (let i = 0; i < 4; i++) {
          const t = new THREE.Mesh(tentacleGeo, this.accentMaterial);
          const a = (i / 4) * Math.PI * 2;
          t.position.set(Math.cos(a) * 0.32, -0.25, Math.sin(a) * 0.32);
          tentacles.add(t);
        }
        accents.push(tentacles);
        group.add(body, tentacles);
        break;
      }
      case 'bear': {
        body = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.9, 0.9), this.inhabitantBaseMaterial);
        const head = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.55, 0.55), this.accentMaterial);
        head.position.set(0, 0.65, 0.1);
        accents.push(head);
        group.add(body, head);
        break;
      }
      case 'fox': {
        body = new THREE.Mesh(new THREE.ConeGeometry(0.6, 1.0, 6), this.inhabitantBaseMaterial);
        const tail = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.7, 4), this.accentMaterial);
        tail.position.set(-0.45, 0.2, 0);
        tail.rotation.z = Math.PI / 3;
        accents.push(tail);
        group.add(body, tail);
        break;
      }
      case 'star': {
        body = new THREE.Mesh(new THREE.OctahedronGeometry(0.7, 0), this.inhabitantBaseMaterial);
        group.add(body);
        break;
      }
      case 'boat': {
        body = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.32, 0.55), this.inhabitantBaseMaterial);
        const head = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.5, 4), this.accentMaterial);
        head.position.set(0.78, 0.1, 0);
        head.rotation.z = -Math.PI / 2;
        const streamer = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.5, 0.06), this.accentMaterial);
        streamer.position.set(0, 0.4, 0);
        accents.push(head, streamer);
        group.add(body, head, streamer);
        break;
      }
      default:
        body = new THREE.Mesh(new THREE.IcosahedronGeometry(0.6, 0), this.inhabitantBaseMaterial);
        group.add(body);
    }
    return { group, body, accents };
  }

  triggerEmotion(emotion: 'happy' | 'sad' | 'angry' | 'neutral' | 'curious', emojiText: string): void {
    this.emotionTimer = 1.5;

    const palette: Record<string, number> = {
      happy: 0xffe27a,
      sad: 0x7aa9ff,
      angry: 0xff5a3c,
      curious: 0x7affd4,
      neutral: 0xffffff
    };
    const glow = palette[emotion] ?? 0xffffff;
    this.inhabitantBaseMaterial.emissive.setHex(glow);
    this.inhabitantBaseMaterial.emissiveIntensity = 1.0;

    if (emotion === 'angry' || emotion === 'happy') {
      this.shakeTimer = 0.6;
      this.shakeAmplitude = emotion === 'angry' ? 0.18 : 0.1;
    }

    this.spawnEmojiSprite(emojiText);
  }

  private spawnEmojiSprite(emojiText: string): void {
    if (this.emojiSprite) {
      this.group.remove(this.emojiSprite);
      this.emojiSprite.material.dispose();
      const t = (this.emojiSprite.material as THREE.SpriteMaterial).map;
      t?.dispose();
      this.emojiSprite = null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.font = '96px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emojiText, 64, 70);
    const tex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
    const sprite = new THREE.Sprite(mat);
    sprite.position.set(0, 2.4, 0);
    sprite.scale.set(1.4, 1.4, 1.4);
    this.group.add(sprite);
    this.emojiSprite = sprite;
  }

  update(t: number, dt: number): void {
    const bob = Math.sin(t * 0.8 + this.bobPhase) * 0.18;
    this.group.position.y = this.origin.y + bob;

    this.inhabitant.rotation.y += dt * 0.4;

    const breathPhase = t * 1.6 + this.idlePhase;
    const breathScale = 1 + Math.sin(breathPhase) * 0.04;
    this.bodyMesh.scale.setScalar(breathScale);

    this.applyIdleAccent(t);

    if (this.emotionTimer > 0) {
      this.emotionTimer -= dt;
      const k = Math.max(0, this.emotionTimer / 1.5);
      this.inhabitantBaseMaterial.emissiveIntensity = k;
      if (this.emojiSprite) {
        this.emojiSprite.position.y = 2.4 + (1.5 - this.emotionTimer) * 0.8;
        (this.emojiSprite.material as THREE.SpriteMaterial).opacity = k;
      }
      if (this.emotionTimer <= 0) {
        this.inhabitantBaseMaterial.emissive.setHex(0x000000);
        this.inhabitantBaseMaterial.emissiveIntensity = 0;
      }
    }

    if (this.shakeTimer > 0) {
      this.shakeTimer -= dt;
      const phase = (1 - this.shakeTimer / 0.6) * Math.PI * 8;
      this.inhabitant.position.x = Math.sin(phase) * this.shakeAmplitude;
    } else {
      this.inhabitant.position.x = 0;
    }
  }

  private applyIdleAccent(t: number): void {
    const phase = t * 1.2 + this.idlePhase;
    switch (this.personaId) {
      case 'whale': {
        const tail = this.accentParts[0];
        if (tail) tail.rotation.z = Math.PI / 2 + Math.sin(phase * 0.8) * 0.08;
        break;
      }
      case 'jelly': {
        const tentacles = this.accentParts[0];
        if (tentacles) tentacles.position.y = -0.05 + Math.sin(phase * 1.4) * 0.06;
        break;
      }
      case 'bear': {
        const head = this.accentParts[0];
        if (head) head.rotation.z = Math.sin(phase * 0.5) * 0.06;
        break;
      }
      case 'fox': {
        const tail = this.accentParts[0];
        if (tail) tail.rotation.z = Math.PI / 3 + Math.sin(phase * 1.6) * 0.18;
        break;
      }
      case 'star': {
        if (this.emotionTimer <= 0) {
          this.inhabitantBaseMaterial.emissive.setHex(0xffeb88);
          this.inhabitantBaseMaterial.emissiveIntensity = 0.18 + (Math.sin(phase * 1.8) + 1) * 0.12;
        }
        break;
      }
      case 'boat': {
        const streamer = this.accentParts[1];
        if (streamer) streamer.rotation.z = Math.sin(phase * 2.2) * 0.4;
        const head = this.accentParts[0];
        if (head) head.position.y = 0.1 + Math.sin(phase * 1.6) * 0.04;
        break;
      }
    }
  }
}
